import React, { useState, useEffect } from 'react';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [liveCalls, setLiveCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    fetchStats();
    fetchLiveCalls();
    const interval = setInterval(fetchLiveCalls, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveCalls = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/live-calls`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLiveCalls(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching live calls:', error);
      setLiveCalls([]);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      if (response.ok) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordSuccess('');
        }, 2000);
      } else {
        const error = await response.json();
        setPasswordError(error.detail || 'Failed to change password');
      }
    } catch (error) {
      setPasswordError('Error changing password');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!stats) return <div className="error">No data available</div>;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <button className="btn-primary" onClick={() => setShowPasswordModal(true)}>
          Change Password
        </button>
      </div>

      <div className="stats-grid">
        {stats.role === 'admin' ? (
          <>
            <div className="stat-card">
              <h3>Total Revenue</h3>
              <p className="stat-value">${stats.total_revenue?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="stat-card">
              <h3>Active Customers</h3>
              <p className="stat-value">{stats.active_customers || 0}</p>
            </div>
            <div className="stat-card">
              <h3>Total Calls</h3>
              <p className="stat-value">{stats.total_calls || 0}</p>
            </div>
            <div className="stat-card">
              <h3>Call Duration</h3>
              <p className="stat-value">{stats.total_duration || 0} min</p>
            </div>
          </>
        ) : (
          <>
            <div className="stat-card">
              <h3>Current Balance</h3>
              <p className="stat-value">${stats.balance?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="stat-card">
              <h3>Total Calls</h3>
              <p className="stat-value">{stats.total_calls || 0}</p>
            </div>
            <div className="stat-card">
              <h3>Total Usage</h3>
              <p className="stat-value">${stats.total_usage?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="stat-card">
              <h3>Call Duration</h3>
              <p className="stat-value">{stats.total_duration || 0} min</p>
            </div>
          </>
        )}
      </div>

      {/* Live Calls Section */}
      <div className="live-calls-section">
        <h2>
          Live Calls 
          {liveCalls.length > 0 && <span className="live-indicator">● {liveCalls.length}</span>}
        </h2>
        {liveCalls.length === 0 ? (
          <div className="no-live-calls">No active calls</div>
        ) : (
          <table className="live-calls-table">
            <thead>
              <tr>
                {stats.role === 'admin' && <th>CUSTOMER</th>}
                <th>CALLER ID</th>
                <th>DESTINATION</th>
                <th>DURATION</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {liveCalls.map((call, index) => (
                <tr key={call._id || index} className="live-call-row">
                  {stats.role === 'admin' && <td>{call.company_name || 'Unknown'}</td>}
                  <td>{call.caller_id || 'N/A'}</td>
                  <td>{call.destination || 'N/A'}</td>
                  <td>{call.duration || 0}s</td>
                  <td>
                    <span className="badge badge-active">
                      <span className="pulse-dot"></span>
                      ACTIVE
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)} className="modal-close">&times;</button>
            </div>
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  required
                />
              </div>

              {passwordError && <div className="error-message">{passwordError}</div>}
              {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}

              <div className="modal-footer">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">Change Password</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
